<template>
  <article class="insurance-claim-card" :class="{ 'insurance-claim-card--expanded': expanded }">
    <div class="insurance-claim-card__header">
      <div class="insurance-claim-card__icon" aria-hidden="true">
        <img v-if="iconSrc" :src="iconSrc" alt="" class="insurance-claim-card__icon-image" />
        <i v-else :class="icon" />
      </div>

      <DsText as="subtitle6" class="insurance-claim-card__title">
        {{ expanded ? titleOpenComputed : titleClosedComputed }}
      </DsText>
    </div>

    <DsText as="subtitle14" class="insurance-claim-card__description">
      {{ description }}
    </DsText>

    <div v-if="expanded" class="insurance-claim-card__details">
      <slot />
    </div>

    <button
      type="button"
      class="insurance-claim-card__toggle"
      :aria-expanded="expanded"
      @click="toggleExpanded"
    >
      <span class="insurance-claim-card__toggle-text">{{ expanded ? 'Ocultar requisitos' : toggleLabel }}</span>
      <img
        :src="arrowDownIcon"
        alt=""
        class="insurance-claim-card__chevron"
        aria-hidden="true"
      />
    </button>

  </article>
</template>

<script setup lang="ts">
import { computed, ref, withDefaults } from 'vue'
import DsText from '../atoms/DsText.vue'
import arrowDownIcon from '../../../../../assets/Seguros/Seguros_arrow_down.svg'

interface Props {
  title: string
  description: string
  titleOpen?: string
  titleClosed?: string
  icon?: string
  iconSrc?: string
  defaultExpanded?: boolean
  toggleLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  titleOpen: undefined,
  titleClosed: undefined,
  icon: 'fa-solid fa-circle-check',
  iconSrc: undefined,
  defaultExpanded: false,
  toggleLabel: 'Ver requisitos',
})

const expanded = ref(props.defaultExpanded)

const titleOpenComputed = computed(() => props.titleOpen ?? props.title)
const titleClosedComputed = computed(() => props.titleClosed ?? props.title)

const toggleExpanded = () => {
  expanded.value = !expanded.value
}
</script>

<style scoped>
.insurance-claim-card {
  width: 100%;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(15, 24, 38, 0.05);
  border-radius: 28px;
  box-shadow: 0 10px 28px rgba(15, 24, 38, 0.05);
  padding: 28px 28px 20px;
  box-sizing: border-box;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.insurance-claim-card--expanded {
  box-shadow: 0 12px 30px rgba(15, 24, 38, 0.08);
}

.insurance-claim-card__header {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 18px;
}

.insurance-claim-card__icon {
  width: 40px;
  height: auto;
  display: grid;
  place-items: center;
  border-radius: 18px;
  color: #f59e0b;
  font-size: 28px;
  line-height: 1;
}

.insurance-claim-card__icon i {
  display: block;
}



.insurance-claim-card__description {
  margin: 0;
  color: #0f172a;

  line-height: 1.35;
  font-weight: 400;
  max-width: 700px;
}

.insurance-claim-card__toggle {
  width: 100%;
  margin-top: 12px;
  background: transparent;
  border: none;
  padding: 18px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #5b4cc9;
  cursor: pointer;
  font: inherit;
}

.insurance-claim-card__toggle-text {
  font-size: var(--ds-subtitle7-size);
  color: var(--ds-purple-main);
  font-weight: var(--ds-subtitle6-weight);
}

.insurance-claim-card__chevron {
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;
}

.insurance-claim-card--expanded .insurance-claim-card__chevron {
  transform: rotate(180deg);
}

.insurance-claim-card__details {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(15, 24, 38, 0.06);
}

:deep(.insurance-claim-card__list) {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

:deep(.insurance-claim-card__list li) {
  position: relative;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 10px;
  align-items: flex-start;
  color: var(--ds-text-primary);
  line-height: 1.5;
}

:deep(.insurance-claim-card__list li::before) {
  content: "";
  width: 18px;
  height: 18px;
  margin-top: 3px;
  background: url('../../../assets/firma-escrituras/icon-check.svg') center / contain no-repeat;
  display: block;
}

@media (max-width: 767px) {
  .insurance-claim-card {
    padding: 16px;
    border-radius: 16px;
    box-shadow: 0 10px 8px rgba(0, 0, 0, 0.1), 0 4px 3px rgba(0, 0, 0, 0.1);
  }

  .insurance-claim-card__header {
    gap: 16px;
    margin-bottom: 16px;
  }

  .insurance-claim-card__icon {
    width: 40px;
    height: 40px;
    min-width: 40px;
    font-size: 22px;
  }

  .insurance-claim-card__icon-image { width: 40px; height: 40px; object-fit: contain; }
  .insurance-claim-card__description { font-size: 14px; line-height: 20px; }
  .insurance-claim-card__details { margin-top: 16px; padding-top: 16px; }
  .insurance-claim-card__toggle { margin-top: 16px; padding-top: 0; min-height: 48px; }
  .insurance-claim-card__toggle-text { font-size: 16px; line-height: 24px; }
  :deep(.insurance-claim-card__list) { gap: 16px; }
  :deep(.insurance-claim-card__list li) { grid-template-columns: 24px minmax(0, 1fr); gap: 4px; font-size: 12px; line-height: 16px; }
  :deep(.insurance-claim-card__list li::before) { width: 24px; height: 24px; margin: 0; }

}
</style>
